import type { WhatsappGroup } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { WhatsappGroupResponseMode, WhatsappGroupStatus } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { BadgeVariant } from '../../symphony/ui/Badge';

// The WhatsApp settings section shows only "live/relevant" groups. DELETED and
// CREATE_FAILED are returned by the API but are noise for the owner, so they are
// filtered out here (mirroring the native hub, which hides DELETED). Keep this
// list as the single source of truth for both the desktop and mobile sections.
export const LIVE_WHATSAPP_GROUP_STATUSES: readonly WhatsappGroupStatus[] = [
  WhatsappGroupStatus.ACTIVE,
  WhatsappGroupStatus.PENDING_CREATE,
  WhatsappGroupStatus.OWNER_LEFT,
];

export const isLiveWhatsappGroupStatus = (status: WhatsappGroupStatus): boolean =>
  LIVE_WHATSAPP_GROUP_STATUSES.includes(status);

export const liveWhatsappGroups = (groups: WhatsappGroup[]): WhatsappGroup[] =>
  groups.filter((group) => isLiveWhatsappGroupStatus(group.status));

// Is anything still waiting on Meta? Drives the groups read's poll (see `useWhatsappGroups`).
//
// Reads the RAW list, never `liveWhatsappGroups`: the sweep that gives up on a lost group flips it to
// CREATE_FAILED, which the filter hides — polling the filtered list would keep asking forever for a
// group that is never coming back.
export const hasPendingWhatsappGroup = (groups: WhatsappGroup[] | undefined): boolean =>
  (groups ?? []).some((group) => group.status === WhatsappGroupStatus.PENDING_CREATE);

export const statusLabelKey = (status: WhatsappGroupStatus): string => {
  switch (status) {
    case WhatsappGroupStatus.PENDING_CREATE:
      return 'settings.whatsapp.group.status.pending';
    case WhatsappGroupStatus.OWNER_LEFT:
      return 'settings.whatsapp.group.status.ownerLeft';
    default:
      return 'settings.whatsapp.group.status.active';
  }
};

export const modeLabelKey = (mode: WhatsappGroupResponseMode): string =>
  mode === WhatsappGroupResponseMode.ALWAYS ? 'settings.whatsapp.group.mode.always' : 'settings.whatsapp.group.mode.mentionOnly';

// The status badge on a group card, per Figma 14077-197325: PENDING_CREATE takes the
// peach "waiting on you" style, ACTIVE the mint "active" one, OWNER_LEFT the quiet
// neutral. Shaped like `taskStatusBadge` — a badge STYLE, never a colour — so the
// render site is `<Badge variant={badge.variant}>`.
//
// ACTIVE is `active`, NOT `connected`: the group frame draws the live pill in the mint
// accent, where the connectors frames still draw Connected in the neon lime. Two Styles,
// not one stale token — design QA reopened this item (Monday 12771180693) on it.
export interface WhatsappGroupBadge {
  labelKey: string;
  variant: BadgeVariant;
}

const STATUS_BADGE: Partial<Record<WhatsappGroupStatus, WhatsappGroupBadge>> = {
  [WhatsappGroupStatus.PENDING_CREATE]: {
    labelKey: 'settings.whatsapp.group.status.pending',
    variant: 'waiting-on-you',
  },
  [WhatsappGroupStatus.OWNER_LEFT]: {
    labelKey: 'settings.whatsapp.group.status.ownerLeft',
    variant: 'waiting-on-a-reply',
  },
};

const ACTIVE_BADGE: WhatsappGroupBadge = {
  labelKey: 'settings.whatsapp.group.status.active',
  variant: 'active',
};

export const whatsappGroupBadge = (status: WhatsappGroupStatus): WhatsappGroupBadge => STATUS_BADGE[status] ?? ACTIVE_BADGE;
